import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Package } from 'lucide-react';
import { DailyMetalData } from '../types/metal';
import { formatNumberDisplay } from '@/lib/utils';

interface MetalFinishedTableProps {
  dailyData: DailyMetalData[];
  isLoading: boolean;
  error: string | null;
  title: string;
}

const MetalFinishedTable: React.FC<MetalFinishedTableProps> = ({ 
  dailyData, 
  isLoading, 
  error, 
  title 
}) => {
  // Extract finished product data (M3 carton and container count)
  const finishedData = useMemo(() => {
    return dailyData.map(day => ({
      date: day.date,
      m3Carton: day.m3Carton || 0,
      containerCount: day.containerCount || 0,
    }));
  }, [dailyData]);

  // Sort data by date (newest first)
  const sortedData = useMemo(() => {
    return [...finishedData].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [finishedData]);

  // Calculate statistics for highlighting (max/min values)
  const stats = useMemo(() => {
    if (sortedData.length === 0) return { m3Max: 0, m3Min: 0, contMax: 0, contMin: 0 };
    
    const m3Values = sortedData.map(d => d.m3Carton).filter(v => v > 0);
    const contValues = sortedData.map(d => d.containerCount).filter(v => v > 0);
    
    return {
      m3Max: m3Values.length > 0 ? Math.max(...m3Values) : 0,
      m3Min: m3Values.length > 0 ? Math.min(...m3Values) : 0,
      contMax: contValues.length > 0 ? Math.max(...contValues) : 0,
      contMin: contValues.length > 0 ? Math.min(...contValues) : 0,
    };
  }, [sortedData]);

  // Format numbers
  // Use Vietnamese locale formatting for numbers

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  // Get cell styling for highlighting (same logic as RecentTable)
  const getCellStyle = (value: number, type: 'm3' | 'container') => {
    if (value === 0) return '';
    
    if (type === 'm3') {
      if (value === stats.m3Max && stats.m3Max > 0) {
        return 'bg-green-600 text-white font-semibold'; // Green for max
      }
      if (value === stats.m3Min && stats.m3Min > 0) {
        return 'bg-red-600 text-white font-semibold'; // Red for min
      }
    } else if (type === 'container') {
      if (value === stats.contMax && stats.contMax > 0) {
        return 'bg-green-600 text-white font-semibold'; // Green for max
      }
      if (value === stats.contMin && stats.contMin > 0) {
        return 'bg-red-600 text-white font-semibold'; // Red for min
      }
    }
    
    return '';
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Lỗi tải dữ liệu: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          M3 carton và số container theo từng ngày
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : sortedData.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center border-r w-[100px]">Ngày</TableHead>
                  <TableHead className="text-center border-r w-[150px] bg-blue-50 font-semibold">M³ Carton</TableHead>
                  <TableHead className="text-center w-[150px] bg-orange-50 font-semibold">Số Container</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((day, index) => (
                  <TableRow key={day.date} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <TableCell className="font-medium text-center border-r">
                      {formatDate(day.date)}
                    </TableCell>
                    <TableCell className={`text-center border-r ${getCellStyle(day.m3Carton, 'm3')}`}>
                                              {formatNumberDisplay(day.m3Carton, 2)}
                    </TableCell>
                    <TableCell className={`text-center ${getCellStyle(day.containerCount, 'container')}`}>
                      {formatNumberDisplay(day.containerCount, 1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Không có dữ liệu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetalFinishedTable;